import React, { useState, useEffect } from "react";
import { useAuth } from "../utils/useAuth";
import { db } from "../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, where, updateDoc } from "firebase/firestore";
import { createManagerNotification } from "../utils/notifications";

export default function CompetitionRequest() {
  const { user, profile } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [competitionName, setCompetitionName] = useState("");

  useEffect(() => {
    loadCompetitions();
  }, [user]);

  async function loadCompetitions() {
    if (!user) return;
    
    try {
      const q = query(collection(db, "competitions"), where("createdBy", "==", user.uid));
      const snap = await getDocs(q);
      setCompetitions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error loading competitions:", err);
      // Silently fail if permissions denied (user may not be approved yet)
    }
  }

  async function addCompetition(e) {
    e.preventDefault();
    
    if (!competitionName) {
      alert("Please enter a competition name");
      return;
    }

    try {
      await addDoc(collection(db, "competitions"), {
        name: competitionName,
        createdBy: user.uid,
        createdByName: profile.fullName,
        approved: false,
        createdAt: serverTimestamp(),
      });

      // Notify managers about new competition request
      await createManagerNotification(
        "New Competition Request",
        `${profile.fullName} has submitted a new competition for approval: ${competitionName}.`,
        profile.union
      );

      alert("Competition submitted for approval!");
      setCompetitionName("");
      loadCompetitions();
    } catch (err) {
      alert("Error adding competition: " + err.message);
    }
  }

  async function deleteCompetition(id) {
    if (!window.confirm("Are you sure you want to delete this competition request?")) return;

    try {
      await deleteDoc(doc(db, "competitions", id));
      alert("Competition request deleted");
      loadCompetitions();
    } catch (err) {
      alert("Error deleting competition: " + err.message);
    }
  }

  async function resubmitCompetition(id) {
    if (!window.confirm("Resubmit this competition for approval?")) return;

    try {
      await updateDoc(doc(db, "competitions", id), {
        rejected: false,
        rejectionReason: null,
        rejectedBy: null,
        rejectedAt: null,
        approved: false,
        resubmittedAt: serverTimestamp(),
      });
      alert("Competition resubmitted for approval!");
      loadCompetitions();
    } catch (err) {
      alert("Error resubmitting competition: " + err.message);
    }
  }

  // Separate pending, approved and rejected
  const pendingCompetitions = competitions.filter(c => !c.approved && !c.rejected);
  const approvedCompetitions = competitions.filter(c => c.approved);
  const rejectedCompetitions = competitions.filter(c => c.rejected);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Competition Request</h2>

      {/* ADD NEW COMPETITION FORM */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Request New Competition</h3>
        <form onSubmit={addCompetition} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Competition Name</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              placeholder="e.g., Super Rugby, Currie Cup, School League"
              value={competitionName}
              onChange={(e) => setCompetitionName(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
          >
            Submit for Approval
          </button>
        </form>
      </div>

      {/* PENDING COMPETITIONS */}
      {pendingCompetitions.length > 0 && (
        <div className="bg-white p-6 rounded shadow mb-6">
          <h3 className="text-lg font-semibold mb-4 text-yellow-700">
            Pending Approval ({pendingCompetitions.length})
          </h3>
          <div className="space-y-3">
            {pendingCompetitions.map(comp => (
              <div key={comp.id} className="flex justify-between items-center p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded">
                <div>
                  <div className="font-semibold">{comp.name}</div>
                  <div className="text-sm text-gray-600">
                    Submitted: {comp.createdAt?.toDate().toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => deleteCompetition(comp.id)}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* APPROVED COMPETITIONS */}
      {approvedCompetitions.length > 0 && (
        <div className="bg-white p-6 rounded shadow mb-6">
          <h3 className="text-lg font-semibold mb-4 text-green-700">
            Approved Competitions ({approvedCompetitions.length})
          </h3>
          <div className="space-y-2">
            {approvedCompetitions.map(comp => (
              <div key={comp.id} className="flex justify-between items-center p-3 bg-green-50 border-l-4 border-green-600 rounded">
                <div>
                  <div className="font-semibold">{comp.name}</div>
                  <div className="text-sm text-gray-600">
                    Approved: {comp.approvedAt?.toDate().toLocaleDateString()}
                  </div>
                </div>
                <span className="px-3 py-1 text-sm bg-green-600 text-white rounded">
                  ✓ Approved
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REJECTED COMPETITIONS */}
      {rejectedCompetitions.length > 0 && (
        <div className="bg-white p-6 rounded shadow">
          <h3 className="text-lg font-semibold mb-4 text-red-700">
            Rejected Requests ({rejectedCompetitions.length})
          </h3>
          <div className="space-y-3">
            {rejectedCompetitions.map(comp => (
              <div key={comp.id} className="p-3 bg-red-50 border-l-4 border-red-600 rounded">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-semibold">{comp.name}</div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => resubmitCompetition(comp.id)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Resubmit
                    </button>
                    <button
                      onClick={() => deleteCompetition(comp.id)}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {comp.rejectionReason && (
                  <div className="text-sm text-red-700 bg-red-100 p-2 rounded mt-2">
                    <strong>Reason:</strong> {comp.rejectionReason}
                  </div>
                )}
                <div className="text-xs text-gray-600 mt-1">
                  Rejected: {comp.rejectedAt?.toDate().toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EMPTY STATE */}
      {competitions.length === 0 && (
        <div className="bg-gray-50 p-8 rounded text-center text-gray-600">
          <p>You haven't submitted any competition requests yet.</p>
          <p className="text-sm mt-2">Use the form above to request a new competition.</p>
        </div>
      )}
    </div>
  );
}
